import { useEffect, useState } from "react"

import { ProjectConfiguration, VectorSearchQuery } from "@vertesia/common"
import { FilterX, Upload } from "lucide-react"
import { toast } from "sonner"

import { ContentDispositionButton } from "@components/buttons/ContentDispositionButton"
import { UploadDocumentsButton } from "@components/buttons/UploadDocumentsButton"
import { DocumentDataTable } from "@components/datatables/DocumentDataTable"
import { FiltersPanel } from "@components/filters/FiltersPanel"
import { DocumentGrid } from "@components/grids/DocumentGrid"

import { LAST_DISPLAYED_VIEW } from "@constants/cookies"

import { transformFacetStatusResponses, transformFacetTypeResponses } from "@lib/facet"

import { FacetResponse } from "@models/facets"

import { useWatchDocumentSearchFacets, useWatchDocumentSearchResult } from "@plugins/providers/document/DocumentSearchContext"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"

interface SearchFilters {
    [key: string]: any
}

export function DocumentSearchPanel() {
    const { hasMore, search, isSearching, error, objects } = useWatchDocumentSearchResult()
    const facets = useWatchDocumentSearchFacets()

    const { client, project, typeRegistry } = useUserSession()

    const [config, setConfig] = useState<ProjectConfiguration | undefined>(undefined)
    const embeddingsReady = !!project && (!!config?.embeddings.text || !!config?.embeddings.image)

    const [isGridView, setIsGridView] = useState(localStorage.getItem(LAST_DISPLAYED_VIEW) === "grid")

    const [filters, setFilters] = useState<SearchFilters>({})
    const [statuses, setStatuses] = useState<FacetResponse[]>([])
    const [types, setTypes] = useState<FacetResponse[]>([])
    const [users, setUsers] = useState<FacetResponse[]>([])

    const [loadingMore, setLoadingMore] = useState(false)
    const [isReady, setIsReady] = useState(true)

    const [clearFilters, setClearFilters] = useState(0)

    const generateTextEmbeddings = async (vectorText: string) => {
        if (!embeddingsReady || !vectorText || !config?.embeddings.text) {
            return
        }

        const response = await client.environments.embeddings(config.embeddings.text?.environment, { model: config.embeddings.text?.model, text: vectorText })

        const query: VectorSearchQuery = {
            values: response.values,
            config: {
                text: true,
                image: true,
                properties: true,
            }
        }

        return query
    }

    const searchObjects = async () => {
        const vectorText = filters?.vectorText?.value as string
        const vector: VectorSearchQuery | undefined = (vectorText ? await generateTextEmbeddings(vectorText) : undefined) as VectorSearchQuery

        const query: any = filters
            ? {
                createdFrom: filters?.date?.createdFrom,
                createdTo: filters?.date?.createdTo,
                updatedFrom: filters?.date?.updatedFrom,
                updatedTo: filters?.date?.updatedTo,
                full_text: filters?.property?.fullText,
                name: filters?.property?.name,
                status: filters?.status,
                type: filters?.type,
                vector,
                match: { created_by: filters?.user },
            }
            : {}

        search.query = query

        search.search()
    }

    const loadMoreDocuments = () => {
        setLoadingMore(true)
        setIsReady(false)

        search
            .loadMore()
            .then(() => {
                setLoadingMore(false)
                setIsReady(true)
            })
            .catch(() => {
                setLoadingMore(false)
            })
    }

    const handleOnClearFilters = () => {
        setClearFilters(clearFilters + 1)
    }

    useEffect(() => {
        if (!client || !project) {
            return
        }

        client.projects.retrieve(project.id).then((project) => {
            setConfig(project.configuration)
        })
    }, [project, client])

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error while searching documents", {
            description: error.message,
            duration: 5000,
        })
    }, [error])

    useEffect(() => {
        setTypes(transformFacetTypeResponses(facets.type, typeRegistry))
        setStatuses(transformFacetStatusResponses(facets.status))
        setUsers(transformFacetStatusResponses((facets as any).created_by))
    }, [facets])

    useEffect(() => {
        searchObjects()
    }, [])

    return (
        <div className="flex h-full w-full">
            <FiltersPanel statuses={statuses} types={types} users={users} onFiltersChange={setFilters} onSearch={() => searchObjects()} onClearFilters={clearFilters} />
            <div className="w-full h-full flex flex-col gap-4 p-4">
                <div className="w-full flex flex-row gap-2 items-center justify-end">
                    <div className="flex gap-4 items-center">
                        {filters && Object.keys(filters).length > 0 && (
                            <Button onClick={handleOnClearFilters}>
                                <FilterX />
                                <span>Clear Filter{Object.keys(filters).length > 1 ? "s" : ""}</span>
                                <span className="text-xs text-blue-500">({Object.keys(filters).length})</span>
                            </Button>
                        )}
                        <UploadDocumentsButton onUploadDone={() => searchObjects()}>
                            <Button>
                                <Upload />
                                <span>Upload Document(s)</span>
                            </Button>
                        </UploadDocumentsButton>
                        <ContentDispositionButton onUpdate={setIsGridView} />
                    </div>
                </div>
                <div className="flex-grow w-full overflow-auto">
                    {
                        isGridView
                            ? <DocumentGrid documents={objects} loading={isSearching} loadingMore={loadingMore} hasMore={hasMore} loadMore={loadMoreDocuments} />
                            : <DocumentDataTable data={objects} loading={isSearching} hasMore={hasMore && isReady} loadMore={loadMoreDocuments} />
                    }
                </div>
            </div>
        </div>
    )
}
